/**
 * Stripe Payment Service
 * 
 * IMPORTANT: This service uses placeholder Stripe keys.
 * Before processing real payments, you must:
 * 1. Create a Stripe account at https://stripe.com
 * 2. Get your API keys from https://dashboard.stripe.com/apikeys
 * 3. Replace placeholder keys in .env file with your actual keys
 * 4. Test with Stripe test cards: https://stripe.com/docs/testing
 */

const STRIPE_PUBLISHABLE_KEY = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;

// Check if Stripe keys are still placeholders
const isPlaceholderKey = () => {
  return !STRIPE_PUBLISHABLE_KEY || 
         STRIPE_PUBLISHABLE_KEY?.includes('placeholder') || 
         STRIPE_PUBLISHABLE_KEY === 'your-stripe-publishable-key-here';
};

// Load Stripe.js dynamically
let stripePromise = null;

const getStripe = () => {
  if (isPlaceholderKey()) {
    console.warn('⚠️ Using placeholder Stripe keys. Replace with actual keys before processing real payments.');
    return null;
  }

  if (!stripePromise) {
    // Dynamically load Stripe.js
    if (window.Stripe) {
      stripePromise = window.Stripe(STRIPE_PUBLISHABLE_KEY);
    } else {
      console.error('Stripe.js not loaded. Add <script src="https://js.stripe.com/v3/"></script> to index.html');
    }
  }
  
  return stripePromise;
};

/**
 * Process invoice payment with Stripe
 * @param {Object} invoiceData - Invoice data including amount and customer info
 * @returns {Promise<Object>} Payment result
 */
export const processInvoicePayment = async (invoiceData) => {
  // Check for placeholder keys
  if (isPlaceholderKey()) {
    return {
      success: false,
      message: 'Stripe integration not configured. Please add your Stripe API keys to the .env file.',
      setupRequired: true
    };
  }

  const stripe = getStripe();
  if (!stripe) {
    return {
      success: false,
      message: 'Stripe failed to initialize. Please check your configuration.',
      error: 'STRIPE_INIT_FAILED'
    };
  }

  try {
    // TODO: Implement actual Stripe payment processing
    // This will require backend integration for secure payment processing
    console.log('Processing payment for invoice:', invoiceData);

    // Placeholder response - replace with actual Stripe API call
    return {
      success: false,
      message: 'Payment processing requires backend implementation with Stripe API',
      setupRequired: true,
      note: 'This is a placeholder. Implement server-side Stripe integration for secure payment processing.'
    };

  } catch (error) {
    console.error('Stripe payment error:', error);
    return {
      success: false,
      message: error?.message || 'Payment processing failed',
      error: error
    };
  }
};

/**
 * Create a payment intent for invoice
 * Note: This should be called from backend for security
 */
export const createPaymentIntent = async (amount, currency = 'usd', metadata = {}) => {
  if (isPlaceholderKey()) {
    return {
      success: false,
      message: 'Stripe keys not configured',
      setupRequired: true
    };
  }

  // TODO: This should be a backend API call, not client-side
  console.warn('⚠️ Payment intents should be created server-side for security');
  
  return {
    success: false,
    message: 'Payment intent creation requires backend implementation',
    setupRequired: true
  };
};

/**
 * Check if Stripe is properly configured
 */
export const isStripeConfigured = () => {
  return !isPlaceholderKey() && STRIPE_PUBLISHABLE_KEY?.startsWith('pk_');
};

/**
 * Get Stripe configuration status
 */
export const getStripeStatus = () => {
  if (isPlaceholderKey()) {
    return {
      configured: false,
      message: 'Stripe integration uses placeholder keys',
      action: 'Add your Stripe API keys to .env file'
    };
  }

  if (!STRIPE_PUBLISHABLE_KEY?.startsWith('pk_')) {
    return {
      configured: false,
      message: 'Invalid Stripe publishable key format',
      action: 'Check your VITE_STRIPE_PUBLISHABLE_KEY in .env file'
    };
  }

  return {
    configured: true,
    message: 'Stripe is configured and ready',
    testMode: STRIPE_PUBLISHABLE_KEY?.includes('_test_')
  };
};

// Export the service object with all methods
export const stripeService = {
  processInvoicePayment,
  createPaymentIntent,
  isStripeConfigured,
  getStripeStatus
};

export default stripeService;